import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { StoreContext } from "../contexts/store.context";
import { StoreApi } from "../api/store";
import { useStoreView } from "../hooks/useStoreView";
import type { CategoryType, OrderingOptionType, PaginationType, StoreItemType } from "../types";

interface StoreProps {
    children: ReactNode;
}

export const StoreProvider = ({ children }: StoreProps) => {
    const [isFetching, setIsFetching] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string>(undefined)

    const [categories, setCategories] = useState<CategoryType[]>([])
    const [items, setItems] = useState<StoreItemType[]>([])
    const [pagination, setPagination] = useState<PaginationType>(undefined)
    const [availableOrdering, setAvailableOrdering] = useState<OrderingOptionType[]>([])
    const [availableFiltering, setAvailableFiltering] = useState<any>(undefined)
    const [orderBy, setOrderBy] = useState<OrderingOptionType>(undefined)
    const [showFilters, setShowFilters] = useState<boolean>(true)
    const [searchTerm, setSearchTerm] = useState<string>(undefined)
    const [page, setPage] = useState<number>(1)
    const [attributeFilters, setAttributeFilter] = useState<number[]>([])
    const { view, setView } = useStoreView()

    const [selectedCategory, setSelectedCategory] = useState<CategoryType | undefined>(undefined)

    const selectCategory = (name) => {
        const category = categories.find((c) => c.name == name)
        if (category) {
            setSelectedCategory(category)
        }
    }

    const appendItems = (data: StoreItemType[]) => {
        setItems(items => {
            const newItems = data.filter(d => items.find(i => i.uuid == d.uuid) === undefined)
            return [...items, ...newItems]
        })
    }

    const contextValue = {
        api: StoreApi,
        categories,
        items,
        pagination,
        page,
        setPage,
        isFetching,
        errorMessage,
        setErrorMessage,
        selectedCategory,
        setSelectedCategory,
        selectCategory,
        showFilters,
        setShowFilters,
        availableOrdering,
        setAvailableOrdering,
        orderBy,
        setOrderBy,
        searchTerm,
        setSearchTerm,
        availableFiltering,
        attributeFilters,
        setAttributeFilter,
        view,
        setView,
    }

    useEffect(() => {
        contextValue.api.fetchCategories().then((categories) => {
            setCategories(categories)
            setSelectedCategory(categories.find(c => c.count > 0))
        })
    }, [])

    useEffect(() => {
        const requestController = new AbortController()

        setIsFetching(true)
        setItems([])
        setPage(1)

        const fetchOptions = {
            category_id: searchTerm ? undefined : selectedCategory?.id,
            page,
            order_by: orderBy?.value,
            search: searchTerm,
            attributes: attributeFilters,
            signal: requestController.signal,
        }

        contextValue.api.fetchItems(fetchOptions).then(result => {
            setItems(result.items)
            setPagination(result.pagination)
            setAvailableOrdering(result.available_ordering)
            setAvailableFiltering(result.available_filtering)
        }).catch(error => {
            setErrorMessage(error.name)
        }).finally(() => setIsFetching(false))

        return () => {
            requestController.abort()
        }
    }, [selectedCategory, orderBy, searchTerm, attributeFilters])

    useEffect(() => {
        if (selectedCategory) {
            setIsFetching(true)
            const fetchOptions = { category_id: selectedCategory.id, page, order_by: orderBy?.value, search: searchTerm, signal: undefined }
            contextValue.api.fetchItems(fetchOptions).then(result => {
                appendItems(result.items)
                setPagination(result.pagination)
            }).finally(() => setIsFetching(false))
        }
    }, [page])

    useEffect(() => {
        setAttributeFilter([]);
    }, [searchTerm, selectedCategory])


    return <StoreContext.Provider value={contextValue}>
        {children}
    </StoreContext.Provider>
}